import { includes } from 'lodash';
import { MARKET } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useAnimation } from 'framer-motion';
import { useCompetition, useRootStore } from '@nandosaus/state-management';
import { useEffect, useState } from 'react';

import { logger } from '@utils/logger';
import { Alert } from '../alert';
import { Auth } from '../../../lib/amplify';
import { Box, Col, Container, Flex } from '../grid';
import { Button } from '../button';
import { ButtonHelper } from './components/primary-button-helper';
import { Checkbox } from '../input/checkbox';
import { HeadingWithLineHighlights } from '../heading-with-line-highlights';
import { InactiveCompetitionContent } from './inactive-competition-content';
import { Link } from '../link';
import { LoadingIndicator } from '../icons/loading-indicator';
import { LoseModal } from './lose-modal';
import { navigation } from '../../constants';
import { P } from '../typography';
import { PrizeModal } from './prize-modal';
import { Spinner } from './spinner';
import { STATUS } from './constants';
import { useIsClient } from '../../hooks/use-is-client';
import { useMarket } from '../../hooks/use-market';
import { useSpin } from './hooks/use-spin';

const SpinToWinContent = () => {
  const market = useMarket();
  const isAustralia = market === MARKET.AUSTRALIA;
  const { MemberStore } = useRootStore();
  const { loading: isLoadingMember, profile } = MemberStore;

  const {
    endDate,
    entries,
    isActive: hasActiveCompetition,
    isLoading: isLoadingCompetition,
    userCanEnter,
  } = useCompetition();

  const controls = useAnimation();
  const isClient = useIsClient();

  const {
    doSpin,
    errorMessage,
    status,
    setStatus,
    prize,
    hasConsentToEmailGuest,
    hasMemberRecentlyEntered,
    setHasConsentToEmailGuest,
  } = useSpin({ controls });

  const isModalOpen = includes([STATUS.WIN, STATUS.LOSE], status);
  const highlightHeadingLine = isModalOpen ? 'alwaysHighlightFirstLine' : 'alwaysHighlightLastLine';

  const [isLoading, setIsLoading] = useState(true);
  const [showButtonHelper, setShowButtonHelper] = useState(false);

  const handleSpinButtonClick = () => {
    setShowButtonHelper(false);
    doSpin();
  };

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .catch(reason => logger.info('Auth', { location: 'spin to win', reason }))
      .finally(() => setIsLoading(false));
  }, []);

  const endDateString = new Date(endDate).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (!isClient || isLoading || isLoadingCompetition || isLoadingMember) {
    return (
      <Flex data-testid="loading-indicator" mx="auto" my="12rem">
        <LoadingIndicator />
      </Flex>
    );
  }

  if (!hasActiveCompetition) {
    return (
      <InactiveCompetitionContent
        heading="Nothing to see here!"
        body="This competition has now ended. Sign up to PERi-Perks for your chance to take part in future competitions
        &amp; stay tuned for more great comps"
        showButtonHelper={showButtonHelper}
        setShowButtonHelper={setShowButtonHelper}
      />
    );
  }

  return (
    <>
      <Container
        py={2}
        textAlign="center"
        display="flex"
        flexDirection={{ xs: 'column', lg: 'row' }}
        alignItems="center"
      >
        <Col
          width={{ xs: 12 / 12, lg: 6 / 12 }}
          display="flex"
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          mb={{ xs: 2, lg: 0 }}
          pt={3}
        >
          <>
            <HeadingWithLineHighlights
              heading={
                hasMemberRecentlyEntered || userCanEnter
                  ? 'Go on, \ngive it a spin!'
                  : 'Sorry, \nyou have no spins left!'
              }
              overrideHeadingHighlighting={[highlightHeadingLine]}
              highlightBackgroundColor="orange400"
            />
            <P maxWidth={{ _: '90%', md: '75%' }} variant={1} mt={2}>
              Every time you purchase a {isAustralia ? `Coca-Cola® product` : `drink`} you’ll receive a spin for the
              chance to win some epic prizes. This promotion ends on {endDateString} unless all prizes are gone earlier.
            </P>
            {!profile && (
              <>
                <P maxWidth={{ _: '90%', md: '75%' }} variant={1} mt={2}>
                  <strong>
                    Not a member? You can still spin, and if you’re a lucky winner you’ll be presented the prize
                    information on the screen.
                  </strong>
                </P>
                <P maxWidth={{ _: '90%', md: '75%' }} variant={1}>
                  If you would prefer to receive your prize via email, you’ll need to opt in to receive the prize
                  information before you play.
                </P>
                <Box mt={2} maxWidth={{ _: '90%', md: '75%' }}>
                  <Checkbox
                    name="consent"
                    label={
                      <P variant={1} textAlign="left">
                        I consent to receive an email from Nando&apos;s regarding prize information. View our{' '}
                        <Link href={navigation.SPIN_TO_WIN_INFO}>terms & conditions</Link>.
                      </P>
                    }
                    checked={hasConsentToEmailGuest}
                    onChange={event => setHasConsentToEmailGuest(!!event.target.checked)}
                  />
                </Box>
              </>
            )}
            <P maxWidth={{ _: '90%', md: '75%' }} variant={1} mt={1}>
              Give it a spin, you’ve earned it.
            </P>
            <Flex flexDirection="column" mt={2} width={{ xs: 12 / 12, lg: 8 / 12 }} justifyContent="center" mx="auto">
              <Box position="relative">
                <Button
                  disabled={entries === 0 || status === STATUS.SPIN}
                  isFullWidth
                  mb="0.5rem"
                  onClick={() => handleSpinButtonClick()}
                  onMouseEnter={() => setShowButtonHelper(true)}
                  onMouseLeave={() => setShowButtonHelper(false)}
                >
                  Spin ({entries} left)
                </Button>
                {showButtonHelper && (
                  <Box position="absolute" right={0} bottom={-6}>
                    <ButtonHelper />
                  </Box>
                )}
              </Box>
              <Button isFullWidth href={navigation.SPIN_TO_WIN_INFO} variant="tertiary">
                More info
              </Button>
            </Flex>
            {errorMessage && (
              <Flex mt={1} justifyContent="center" width="100%">
                <Alert width={{ xs: 12 / 12, lg: 8 / 12 }} type="error">
                  {errorMessage}
                </Alert>
              </Flex>
            )}
          </>
        </Col>
        <Col width={{ xs: 12 / 12, lg: 6 / 12 }}>
          <Spinner controls={controls} />
        </Col>
      </Container>
      {status === STATUS.WIN && (
        <PrizeModal
          entriesAvailable={entries}
          hasConsentToEmailGuest={hasConsentToEmailGuest}
          userCanEnter={userCanEnter}
          onClose={() => setStatus(STATUS.INITIAL)}
          onSpin={doSpin}
          prize={prize}
          profile={profile}
        />
      )}
      {status === STATUS.LOSE && (
        <LoseModal
          entriesAvailable={entries}
          userCanEnter={userCanEnter}
          onClose={() => setStatus(STATUS.INITIAL)}
          onSpin={doSpin}
        />
      )}
    </>
  );
};

SpinToWinContent.propTypes = {};

SpinToWinContent.defaultProps = {};

const ObservedSpinToWinContent = observer(SpinToWinContent);
export { ObservedSpinToWinContent as SpinToWinContent };
